import React from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Map, fromJS } from "immutable";
import { Detail as TableRowDetail } from "MODULES/shared/components/DetailTable";
import removeKey from "LIB/removeKey";
import loadRecord from "MODULES/shared/decorators/loadRecord";
import {
  AuditFields,
  ExternalLink,
  Attachments,
  Notes,
  EditButton,
  PartNumber,
  NoteTooltip,
  LocationFlag
} from "MODULES/shared/components";
import { Attachments as AttachmentsTable } from "MODULES/shared/components/Attachments/Attachments";
import { ATTACHMENT_TYPES as RECEIVING_TYPES } from "MODULES/Receiving/constants";
import resourceTypes from "MODULES/shared/types/resourceTypes";
import { monthYear, monthFullYear, shortDate } from "LIB/formatters/date";
import { dollars } from "LIB/formatters/currency";
import { truncate } from "LIB/formatters/text";
import {
  CURRENTLY_REPAIRING_STATUSES,
  INVENTORIES,
  RESERVE_FOR_SALE,
  RESERVE_FOR_REPAIR,
  ON_ORDER_FOR_SALE,
  SALES_ORDER_CREATED,
  ATTACHMENT_TYPES,
  TAG_TYPES,
  FLAG_TYPES,
  SUGGESTED_RETAIL
} from "MODULES/inventories/constants";
import {
  ACCOUNTING,
  QUALITY_CONTROL,
  WAREHOUSE,
  WAREHOUSE_BASIC,
  WAREHOUSE_EXPERT,
  USER,
  CAN_OVERRIDE_LISTING
} from "MODULES/users/constants";
import Authorized from "MODULES/shared/components/Authorized";
import { INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import { getSellableClass } from "MODULES/shared/components/InventoryStatusCell";
import { Col } from "react-bootstrap";
import { percent } from "LIB/formatters/number";
import TableDetailRow from "MODULES/shared/components/DetailTable/Detail";
import {
  disableIfHasAnyOfRoles,
  hasPermission,
  hasSubRole
} from "MODULES/users/helpers";
import {
  inventoryTaggedBy,
  totalCyclesRemaining,
  totalHoursRemaining
} from "MODULES/inventories/helpers";
import PurchaseDetails from "MODULES/inventories/components/Detail/PurchaseDetails";
import AuditedNoteTooltip from "MODULES/shared/components/NoteTooltip/AuditedNoteTooltip";
import { show } from "../../actions";
import AuditTrailTable from "./AuditTrailTable/AuditTrailTable";
import CostAdjustmentTable from "./CostAdjustmentTable/CostAdjustmentTable";
import DummiesTable from "./Dummies/Table";
import InventoryStatusLabel from "./InventoryStatusLabel";
import AddCostAdjustment from "../AddCostAdjustment/AddCostAdjustment";
import ScrapReportStatusLabel from "./ScrapReportStatusLabel";
import ListingOverrideLabel from "./ListingOverrideLabel";
import Header from "./Header";
import EditInventory from "../EditInventory";
import RecentOrderRow from "./RecentOrderRow";
import CreateCommercialInvoice from "./CreateCommercialInvoice";
import { CostWarning } from "./Warnings";

const lockedStatuses = [
  RESERVE_FOR_SALE,
  ON_ORDER_FOR_SALE,
  SALES_ORDER_CREATED
];

const ModToDetails = ({ part, modTo }) => {
  if (!modTo || (modTo && modTo.id === part.id)) {
    return null;
  }

  return [
    <TableRowDetail label="Mod To" value={modTo.part_number} />,
    <TableRowDetail label="Mod From" value={part.part_number} />
  ];
};

const LocationCol = ({ inventory }) => {
  if (inventory.location === "At Shop" && inventory.repair_order_tagged_by) {
    return (
      <Link
        onClick={e => e.stopPropagation()}
        to={`/repair-orders/${inventory.most_recent_repair_order.uuid}`}
      >
        {truncate(`${inventory.repair_order_tagged_by.name}`, 25)}
      </Link>
    );
  }
  return truncate(inventory.location, 12);
};

const ConsignmentSection = ({ inventory }) => {
  if (!inventory.consignment_reference) return null;

  return [
    <table className="table table-condensed">
      <tbody>
        <TableDetailRow
          label="Consignment Ref. Co"
          value={_.get(inventory, "consignment_reference_company.name")}
        />
        <TableDetailRow
          label="Consignment Ref #"
          value={
            inventory.consignment ? (
              <Link to={`/consignments/${inventory.consignment.uuid}`}>
                <span>{inventory.consignment_reference}</span>
              </Link>
            ) : (
              <span>{inventory.consignment_reference}</span>
            )
          }
        />
        <TableDetailRow
          label="Consignment Split"
          value={percent(inventory.consignor_percentage, 100)}
        />
      </tbody>
    </table>,
    <hr className="hr-dashed" />
  ];
};

export class Detail extends React.Component {
  static propTypes = {
    currentUser: PropTypes.shape({}),
    inventory: PropTypes.instanceOf(Map),
    displayCommercialInvoiceModal: PropTypes.bool,
    reset: PropTypes.func
  };

  state = {
    costAdjustmentModalOpen: false,
    createDummyModalOpen: false,
    editInventoryModalOpen: false,
    initialPartFormValues: {},
    commercialInvoiceModalOpen: false
  };

  componentWillReceiveProps({ displayCommercialInvoiceModal }) {
    if (displayCommercialInvoiceModal) {
      this.setState({ commercialInvoiceModalOpen: true });
    }
  }

  open = () => {
    this.setState({ commercialInvoiceModalOpen: true });
  };

  closeCommercialInvoiceModal = () => {
    this.setState({ commercialInvoiceModalOpen: false });
    this.props.reset();
  };

  openCostAdjustmentModal = () =>
    this.setState({ costAdjustmentModalOpen: true });

  closeCostAdjustmentModal = () =>
    this.setState({ costAdjustmentModalOpen: false });

  openEditInventoryModal = () =>
    this.setState({ editInventoryModalOpen: true });

  closeEditInventoryModal = () =>
    this.setState({ editInventoryModalOpen: false });

  scrapReportSection = inventory => {
    if (!inventory.scrap_report) return null;

    return [
      <tr key="scrap-report-status">
        <th className="text-right">Scrap Report Status</th>
        <td>
          <ScrapReportStatusLabel
            scrapReportStatus={inventory.scrap_report_received}
          />
        </td>
      </tr>,
      <tr key="download-link">
        <th className="text-right">Scrap Report</th>
        <td>
          {inventory.scrap_report && (
            <ExternalLink url={inventory.scrap_report}>
              Download Report
            </ExternalLink>
          )}
        </td>
      </tr>
    ];
  };

  render() {
    const inventory = this.props.inventory.toJS();
    const partOfExchange = _.get(
      inventory,
      "most_recent_sales_order_line_item.exchange_sale"
    );
    const {
      costAdjustmentModalOpen,
      editInventoryModalOpen,
      commercialInvoiceModalOpen
    } = this.state;
    const { currentUser } = this.props;

    const lockInventory =
      lockedStatuses.includes(inventory.status) && !partOfExchange;
    const inventoryLocked =
      disableIfHasAnyOfRoles(currentUser, [WAREHOUSE]) &&
      !hasSubRole(currentUser, WAREHOUSE_EXPERT) &&
      (!inventory || lockInventory);

    const tagTypes = Object.keys(TAG_TYPES)
      .filter(t => inventory[t])
      .map(t => (
        <span className="badge" style={{ marginRight: "5px" }}>
          {TAG_TYPES[t].label.toUpperCase()}
        </span>
      ))
      .concat(
        inventory.other_tag_type ? (
          <span className="badge" style={{ marginRight: "5px" }}>
            {inventory.other_tag_type.toUpperCase()}
          </span>
        ) : (
          []
        )
      );

    const flagTypes = Object.keys(FLAG_TYPES)
      .filter(t => inventory[t])
      .map(t =>
        [
          FLAG_TYPES[t].label,
          FLAG_TYPES[t].subLabel && FLAG_TYPES[t].subLabel(inventory)
        ]
          .filter(f => f)
          .join(" - ")
      )
      .join(", ");

    return (
      <div>
        <CreateCommercialInvoice
          inventory={this.props.inventory}
          close={this.closeCommercialInvoiceModal}
          editing={commercialInvoiceModalOpen}
        />
        <Header
          inventory={fromJS(inventory)}
          inventoryLocked={inventoryLocked}
        />
        <div className="row">
          <Col md={3}>
            <div
              className={`panel panel-default ${getSellableClass(
                inventory
              )}-top`}
            >
              <div className="panel-body">
                <h2 style={{ marginTop: 10 }}>
                  Inventory Details
                  {!inventoryLocked &&
                    !(
                      hasSubRole(currentUser, WAREHOUSE_BASIC) &&
                      !hasPermission(currentUser, CAN_OVERRIDE_LISTING)
                    ) && [
                      <EditButton
                        key="edit-inventory-button"
                        handleClick={this.openEditInventoryModal}
                      />,
                      <EditInventory
                        key="inventory-edit-modal"
                        open={editInventoryModalOpen}
                        close={this.closeEditInventoryModal}
                        inventory={fromJS(inventory)}
                        lockInventory={lockInventory}
                      />
                    ]}
                </h2>
                <table className="table table-condensed">
                  <tbody>
                    <tr>
                      <th className="text-right">Part #</th>
                      <td>
                        <PartNumber part={inventory.part} />
                      </td>
                    </tr>
                    <TableDetailRow
                      label="Serial #"
                      value={inventory.serial_number}
                    />
                    <TableDetailRow
                      label="Prev. Exch'd SN"
                      value={inventory.exchange_serial_number}
                      renderIfPresent
                    />
                    <TableDetailRow
                      value={
                        _.get(
                          inventory,
                          "assigned_core_return.serial_number"
                        ) && (
                          <Link
                            to={`/inventories/${_.get(
                              inventory,
                              "assigned_core_return.uuid"
                            )}`}
                          >
                            {_.get(
                              inventory,
                              "assigned_core_return.serial_number"
                            )}
                          </Link>
                        )
                      }
                      label="Core Return SN"
                      renderIfPresent
                    />

                    <tr>
                      <th className="text-right">INV Status</th>
                      <td>
                        <InventoryStatusLabel inventory={inventory} />
                      </td>
                    </tr>
                    <tr>
                      <th className="text-right">Listing Override</th>
                      <td>
                        <ListingOverrideLabel
                          status={inventory.listing_override}
                        />
                      </td>
                    </tr>
                    <TableDetailRow
                      label="CD"
                      value={_.get(
                        INVENTORIES,
                        `condition.${inventory.condition}.label`
                      )}
                    />
                    <TableDetailRow
                      label="QTY"
                      value={inventory.total_quantity}
                    />
                    <TableDetailRow
                      label="Total Hours"
                      value={inventory.total_hours}
                      renderIfPresent
                    />
                    <TableDetailRow
                      label="Total Hours Remaining"
                      value={
                        inventory.total_hours &&
                        totalHoursRemaining(
                          inventory.part,
                          inventory.total_hours
                        )
                      }
                      renderIfPresent
                    />

                    <TableDetailRow
                      label="Total Cycles"
                      value={inventory.total_cycles}
                      renderIfPresent
                    />
                    <TableDetailRow
                      label="Total Cycles Remaining"
                      value={
                        inventory.total_cycles &&
                        totalCyclesRemaining(
                          inventory.part,
                          inventory.total_cycles
                        )
                      }
                      renderIfPresent
                    />
                  </tbody>
                </table>
                {inventory.active_transfer && [
                  <hr className="hr-dashed" />,
                  <table className="table table-condensed">
                    <tbody>
                      <TableDetailRow
                        label="Transfer QTY Shipped"
                        value={inventory.active_transfer.quantity_shipped}
                      />
                      <TableDetailRow
                        label="Transfer QTY Received"
                        value={inventory.active_transfer.quantity_received}
                      />
                      <TableDetailRow
                        label="Transfer Outbound AWB #"
                        value={inventory.active_transfer.latest_awb_number}
                      />
                    </tbody>
                  </table>
                ]}
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="Buyer Credit"
                      value={percent(
                        parseFloat(_.get(inventory, "buyer_credit_pct")),
                        100
                      )}
                    />
                    <TableDetailRow
                      label="Buyer"
                      value={_.get(inventory, "buyer.full_name")}
                    />
                    <TableDetailRow
                      label="Co-Buyer"
                      value={_.get(inventory, "co_buyer.full_name")}
                    />
                    <TableDetailRow
                      label="Third Buyer"
                      value={_.get(inventory, "third_buyer.full_name")}
                    />
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="SRP"
                      value={[
                        dollars(inventory.suggested_retail),
                        <AuditedNoteTooltip
                          field={SUGGESTED_RETAIL}
                          obj={inventory}
                        />
                      ]}
                    />
                    <TableDetailRow
                      label="SRP CD"
                      value={_.get(
                        INVENTORIES,
                        `condition.${inventory.srp_condition}.label`
                      )}
                    />
                    <TableDetailRow
                      label="Exch. SRP"
                      value={dollars(inventory.exchange_srp)}
                    />
                    <TableDetailRow
                      label="Cost"
                      value={dollars(inventory.total_cost)}
                    />
                    <TableDetailRow
                      renderIfPresent
                      label="Consignor Cost"
                      value={
                        Number(inventory.consignor_cost) > 0
                          ? dollars(inventory.consignor_cost)
                          : null
                      }
                    />
                    <TableDetailRow
                      label="Final Freight Conf"
                      value={inventory.freight_complete ? "Yes" : "No"}
                    />
                    <TableDetailRow label="Flags" value={flagTypes} />
                    <TableDetailRow
                      label="Warranty Override"
                      value={inventory.warranty_override}
                    />
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <CostWarning inventory={inventory} />
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="Tag Date"
                      value={monthYear(inventory.tag_date)}
                    />
                    <tr>
                      <th className="text-right">Tagged By</th>
                      <td>{inventoryTaggedBy(inventory)}</td>
                    </tr>
                    <TableDetailRow
                      label="Trace To"
                      value={inventory.traced_to}
                    />
                    <tr>
                      <th className="text-right">Trace Type</th>
                      <td>
                        {_.get(
                          INCOMING_LINE_ITEMS,
                          `traceTypes.${inventory.trace_type}.label`
                        )}
                      </td>
                    </tr>
                    <TableDetailRow label="Tag Types" value={tagTypes} />
                    <ModToDetails
                      part={inventory.part}
                      modTo={inventory.mod_to}
                    />
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="Store Owner"
                      value={
                        <LocationFlag
                          location={inventory.store_owner_id}
                          consignment_type={inventory.consignment_type}
                          inventory={inventory}
                        />
                      }
                    />
                    <TableDetailRow
                      label="Store Loc"
                      value={
                        <LocationFlag location={inventory.store_location_id} />
                      }
                    />
                    <TableDetailRow
                      label="Loc"
                      value={<LocationCol inventory={inventory} />}
                    />
                    {_.get(inventory, "ware_house.name") && (
                      <TableDetailRow
                        label="Warehouse"
                        value={_.get(inventory, "ware_house.name")}
                      />
                    )}
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="DOM"
                      value={monthFullYear(inventory.dom)}
                    />
                    <TableDetailRow
                      label="Exp"
                      value={shortDate(inventory.expiration)}
                    />
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <ConsignmentSection inventory={inventory} />
                <table className="table table-condensed">
                  <tbody>
                    {this.scrapReportSection(inventory)}
                    <tr>
                      <th className="text-right">Received Date</th>
                      <td>{shortDate(inventory.date_received)}</td>
                    </tr>
                    {inventory.receiving_records.map(recRecord => (
                      <tr>
                        <th className="text-right">Receiving #</th>
                        <td>
                          <Link to={`/receiving/${recRecord.uuid}`}>
                            <span>{recRecord.code}</span>
                          </Link>
                          {recRecord.discrepancies.length > 0 &&
                            recRecord.discrepancies.some(r => !r.resolved) && (
                              <span
                                className="label label-danger"
                                style={{ marginLeft: "5px" }}
                              >
                                <NoteTooltip
                                  icon="fa-warning"
                                  color="rgba(255, 255, 255, 1)"
                                  note="Open Discrepancy"
                                  placement="top"
                                />
                              </span>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <PurchaseDetails inventory={inventory} />

                    <RecentOrderRow
                      record={inventory}
                      type="most_recent_repair_order"
                      title="Recent RO#"
                      rootLink="repair-orders"
                    />
                    {removeKey(
                      CURRENTLY_REPAIRING_STATUSES,
                      RESERVE_FOR_REPAIR
                    )[inventory.repair_status] && (
                      <TableDetailRow
                        label="Repair Station"
                        value={_.get(
                          inventory,
                          "most_recent_repair_order.company.name"
                        )}
                      />
                    )}

                    <TableDetailRow
                      label="Recent SO #"
                      value={
                        <Link
                          to={`/sales-orders/${_.get(
                            inventory,
                            "most_recent_sales_order.uuid",
                            _.get(
                              inventory,
                              "most_recent_customer_rma.exchange_details.sales_order.id",
                              _.get(
                                inventory,
                                "split_rma_inventory_details.most_recent_sales_order_line_item.parent_id"
                              )
                            )
                          )}`}
                        >
                          <span>
                            {_.get(
                              inventory,
                              "most_recent_sales_order.code",
                              _.get(
                                inventory,
                                "most_recent_customer_rma.exchange_details.sales_order.code",
                                _.get(
                                  inventory,
                                  "split_rma_inventory_details.most_recent_sales_order_line_item.code"
                                )
                              )
                            )}
                          </span>
                        </Link>
                      }
                    />

                    <RecentOrderRow
                      record={inventory}
                      type="most_recent_managed_repair_order"
                      title="Recent MR#"
                      rootLink="managed-repair-orders"
                    />

                    <RecentOrderRow
                      record={inventory}
                      type="most_recent_cancelled_sales_order"
                      title="Cancelled SO#"
                      rootLink="sales-orders"
                    />
                    <RecentOrderRow
                      record={inventory}
                      type="most_recent_vendor_rma"
                      title="Recent Vendor RMA#"
                      rootLink="rmas"
                    />

                    <RecentOrderRow
                      record={inventory}
                      type="most_recent_customer_rma"
                      rootLink="rmas"
                      title="Recent Cust. RMA#"
                    />
                  </tbody>
                </table>
                <hr className="hr-dashed" />
                <table className="table table-condensed">
                  <tbody>
                    <TableDetailRow
                      label="Split"
                      value={
                        inventory.split_from_inventory_id ? (
                          <Link
                            to={`/inventories/${inventory.split_from_inventory_id}`}
                          >
                            <span>Yes</span>
                          </Link>
                        ) : (
                          "No"
                        )
                      }
                    />
                  </tbody>
                </table>
                <hr className="hr-sm" />
                <AuditFields record={fromJS(inventory)} multiLine />
              </div>
            </div>
          </Col>
          <Col md={9}>
            <h2 style={{ marginTop: 26 }}>
              Cost Adjustments
              <Authorized roles={[ACCOUNTING, QUALITY_CONTROL]}>
                <button
                  key="cost-adjustment"
                  type="button"
                  className="btn btn-primary btn-hollow pull-right"
                  onClick={this.openCostAdjustmentModal}
                >
                  <i className="fa fa-fw fa-plus">&nbsp;</i>Add Cost Adjustment
                </button>
                <AddCostAdjustment
                  key="add-cost-adjustment-modal"
                  close={this.closeCostAdjustmentModal}
                  open={costAdjustmentModalOpen}
                  inventory={inventory}
                />
              </Authorized>
            </h2>
            <CostAdjustmentTable
              costAdjustments={inventory.cost_adjustments}
              inventory={inventory}
            />

            <Notes
              resource={fromJS(inventory)}
              resourceType={resourceTypes.inventory}
              resourceKey="inventory"
            />
            <hr className="hr-dashed" />
            <Attachments
              title="Inventory Attachments"
              multiple
              resource={fromJS(inventory)}
              resourceType={resourceTypes.inventory}
              resourceKey="inventory"
              attachmentTypes={ATTACHMENT_TYPES}
              isInventory
            />

            <AttachmentsTable
              attachments={fromJS(inventory.receiving_attachments)}
              attachmentTypes={RECEIVING_TYPES}
              multiple
              title="Receiving Attachments"
              preloaded
              disabled
            />

            {inventory.dummy && [
              <DummiesTable key="dummy-table" dummy={inventory.dummy} />,
              <hr key="dummy-seperator" className="hr-dashed" />
            ]}

            <AuditTrailTable auditTrails={inventory.audit_trails} />
          </Col>
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  inventory: state.inventoryDetail.get("inventory"),
  currentUser: state.session.get(USER).toJS(),
  displayCommercialInvoiceModal: state.inventoryDetail.getIn([
    "inventory",
    "displayCommercialInvoiceModal"
  ])
}))(
  loadRecord({
    reducer: "inventoryDetail",
    name: "inventory",
    action: show
  })(Detail)
);



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Detail/Detail.jsx